import {
  CreateMilestoneRequest,
  Milestone,
  MilestoneFilters,
  MilestoneListResponse,
  UpdateMilestoneRequest,
} from "@/pages/StudyMilestones";
import { IMilestoneRepository } from "../repositories/MilestoneRepository";
import type { ApiError } from "../types/auth.type";
import { getStoredUser } from "../utils/auth";
export interface IMilestoneUseCase {
  getMilestones(
    filters?: MilestoneFilters,
    page?: number,
    limit?: number,
  ): Promise<MilestoneListResponse>;
  getMilestoneById(id: string): Promise<Milestone>;
  createMilestone(milestone: CreateMilestoneRequest): Promise<Milestone>;
  updateMilestone(
    id: string,
    updates: UpdateMilestoneRequest,
  ): Promise<Milestone>;
  deleteMilestone(id: string): Promise<void>;
  /**
   * Get milestones by study ID with optional filters
   */
  getMilestonesByStudy(
    studyId: string,
    filters?: Omit<MilestoneFilters, "study_id">,
    page?: number,
    limit?: number,
  ): Promise<MilestoneListResponse>;
}
export class MilestoneUseCase implements IMilestoneUseCase {
  constructor(private milestoneRepository: IMilestoneRepository) {}
  /**
   * Extract meaningful error message from API error response
   */
  private extractErrorMessage(error: unknown): string {
    if (error && typeof error === "object") {
      const err = error as {
        response?: { data?: { message?: string; error?: string } };
        message?: string;
      };
      if (err.response?.data) {
        const data = err.response.data;
        return data.message ?? data.error ?? "An error occurred";
      }
      if (typeof err.message === "string") {
        return err.message;
      }
    }
    return "An unknown error occurred";
  }
  /**
   * Create formatted API error for logging
   */
  private formatApiError(error: unknown): ApiError {
    const err = error as {
      response?: { status?: number; data?: { code?: string } };
    };
    return {
      message: this.extractErrorMessage(error),
      status: err.response?.status ?? 500,
      code: err.response?.data?.code,
    };
  }
  async getMilestones(
    filters?: MilestoneFilters,
    page = 1,
    limit = 10,
  ): Promise<MilestoneListResponse> {
    try {
      // Validate pagination parameters
      if (page < 1) {
        page = 1;
      }
      if (limit < 1 || limit > 100) {
        limit = 10;
      }
      // Default to not showing archived milestones
      const enhancedFilters: MilestoneFilters = {
        ...filters,
        showArchived: filters?.showArchived ?? false,
      };
      return await this.milestoneRepository.getMilestones(
        enhancedFilters,
        page,
        limit,
      );
    } catch (error: unknown) {
      const apiError = this.formatApiError(error);
      throw new Error(apiError.message);
    }
  }
  async getMilestonesByStudy(
    studyId: string,
    filters?: Omit<MilestoneFilters, "study_id">,
    page = 1,
    limit = 10,
  ): Promise<MilestoneListResponse> {
    try {
      if (!studyId?.trim()) {
        throw new Error("Study ID is required");
      }
      // Combine study ID with other filters
      const combinedFilters: MilestoneFilters = {
        studyId,
        ...filters,
        showArchived: filters?.showArchived ?? false,
      };
      return await this.getMilestones(combinedFilters, page, limit);
    } catch (error: unknown) {
      const apiError = this.formatApiError(error);
      throw new Error(apiError.message);
    }
  }
  async getMilestoneById(id: string): Promise<Milestone> {
    try {
      if (!id?.trim()) {
        throw new Error("Milestone ID is required");
      }
      return await this.milestoneRepository.getMilestoneById(id);
    } catch (error: unknown) {
      const apiError = this.formatApiError(error);
      throw new Error(apiError.message);
    }
  }
  async createMilestone(milestone: CreateMilestoneRequest): Promise<Milestone> {
    try {
      // Validate required fields
      if (!milestone.milestoneName?.trim()) {
        throw new Error("Milestone name is required");
      }
      if (!milestone.studyId?.trim()) {
        throw new Error("Study ID is required");
      }
      if (!milestone.plannedDate) {
        throw new Error("Planned date is required");
      }
      // Ensure ownerId is set (use current user if not provided)
      let ownerId = milestone.ownerId;
      if (!ownerId) {
        const currentUser = getStoredUser();
        if (currentUser?.id) {
          ownerId = currentUser.id;
        } else {
          /* no authenticated user — ownerId remains unset */
        }
      }
      const createPayload: CreateMilestoneRequest = {
        milestoneName: milestone.milestoneName,
        studyId: milestone.studyId,
        plannedDate: milestone.plannedDate,
        actualDate: milestone.actualDate ?? undefined,
        status: milestone.status ?? "Planned",
        description: milestone.description ?? undefined,
        notes: milestone.notes ?? undefined,
        ownerId,
      };
      return await this.milestoneRepository.createMilestone(createPayload);
    } catch (error: unknown) {
      const apiError = this.formatApiError(error);
      throw new Error(apiError.message);
    }
  }
  async updateMilestone(
    id: string,
    updates: UpdateMilestoneRequest,
  ): Promise<Milestone> {
    try {
      if (!id?.trim()) {
        throw new Error("Milestone ID is required");
      }
      return await this.milestoneRepository.updateMilestone(id, updates);
    } catch (error: unknown) {
      const apiError = this.formatApiError(error);
      throw new Error(apiError.message);
    }
  }
  async deleteMilestone(id: string): Promise<void> {
    try {
      if (!id?.trim()) {
        throw new Error("Milestone ID is required");
      }
      await this.milestoneRepository.deleteMilestone(id);
    } catch (error: unknown) {
      const apiError = this.formatApiError(error);
      throw new Error(apiError.message);
    }
  }
  /**
   * Calculate variance in days between planned and actual dates
   */
  calculateVariance(plannedDate: string, actualDate: string | null): number {
    if (!actualDate) {
      return 0;
    }
    const planned = new Date(plannedDate);
    const actual = new Date(actualDate);
    const diffTime = actual.getTime() - planned.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }
  /**
   * Determine milestone status based on dates and current status
   */
  determineMilestoneStatus(
    plannedDate: string,
    actualDate: string | null,
    currentStatus: string,
  ): string {
    if (actualDate) {
      const variance = this.calculateVariance(plannedDate, actualDate);
      return variance > 0 ? "Delayed" : "Completed";
    }
    const today = new Date();
    const planned = new Date(plannedDate);
    if (planned < today && !actualDate) {
      return "Delayed";
    }
    return currentStatus || "Planned";
  }
}
